import React from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { api } from "../api";
import { fmtDayLong, fmtTimeRange, kindLabel } from "../lib/dates";
import { useCopy } from "../lib/copy";
import {
  Rule,
  Footer,
  Nav,
  ScatterBand,
  Verse,
  useDocumentTitle,
} from "../components/Chrome";
import Newsletter from "../components/Newsletter";

const fetcher = (url) => api.get(url).then((r) => r.data);

/* The strip that puts the next real thing above everything else. */
function NextStrip() {
  const { data } = useSWR("/gatherings/next", fetcher);
  const c = useCopy();
  const g = data?.gathering;

  if (!g) {
    return (
      <div className="next-strip" data-testid="home-next-strip-empty">
        <div className="inner">
          <span className="lab">Next</span>
          <span className="what">
            {c("home.next.empty_title", "Nothing booked yet")}
          </span>
          <span className="meta">
            <Link to="/join">Tell us you&rsquo;d like to come</Link>{" "}
            {c(
              "home.next.empty_body",
              "and we\u2019ll write when the next thing is set.",
            )}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="next-strip" data-testid="home-next-strip">
      <div className="inner">
        <span className="lab">Next</span>
        <span className="what">
          <Link to={`/gatherings/${g.id}`} style={{ textDecoration: "none" }}>
            {g.title}
          </Link>
        </span>
        <span className="meta">
          {g.when_text
            ? `${g.when_text}, date to be confirmed`
            : `${fmtDayLong(g.starts_at)}, ${fmtTimeRange(g.starts_at, g.ends_at)}`}
          {g.venue ? ` · ${g.venue}` : ""}
          {g.is_tbc ? " · to be confirmed" : ""}
          {" · "}
          <span className="chip">{kindLabel(g.kind)}</span>
        </span>
      </div>
    </div>
  );
}

export default function Home() {
  const c = useCopy();

  useDocumentTitle(
    null,
    "A community of Christian artists in Calgary. We meet to make things, show each other what we've made, and learn to do it better.",
  );

  const doors = [
    {
      to: "/visit",
      icon: "i-hand",
      testid: "home-visit-door-card",
      title: c("home.door.visit.title", "First visit"),
      body: c(
        "home.door.visit.body",
        "What actually happens, what to bring, and whether it\u2019s going to be awkward. The honest version.",
      ),
      go: c("home.door.visit.go", "What you\u2019d be walking into"),
    },
    {
      to: "/gatherings",
      icon: "i-lattice",
      testid: "home-gatherings-door-card",
      title: c("home.door.gatherings.title", "Gatherings"),
      body: c(
        "home.door.gatherings.body",
        "Showings, special evenings and one-offs. What\u2019s coming up, and how to say you\u2019re coming.",
      ),
      go: c("home.door.gatherings.go", "See what\u2019s on"),
    },
    {
      to: "/circles",
      icon: "i-well",
      testid: "home-circles-door-card",
      title: c("home.door.circles.title", "Circles"),
      body: c(
        "home.door.circles.body",
        "Writers with writers, painters with painters. Six crafts, and a place to put your name down for yours.",
      ),
      go: c("home.door.circles.go", "Sign up for a circle"),
    },
    {
      to: "/rule",
      icon: "i-chalice",
      testid: "home-rule-door-card",
      title: c("home.door.rule.title", "The Rule"),
      body: c(
        "home.door.rule.body",
        "The whole shape of the thing: what we mean by Christian art, and the habits we hold each other to. Readable, and downloadable.",
      ),
      go: c("home.door.rule.go", "Read it"),
    },
  ];

  return (
    <>
      <Nav always={false} />

      <header className="hero" data-testid="home-hero">
        <div className="hero-scatter tl" aria-hidden="true" />
        <div className="hero-scatter br" aria-hidden="true" />
        <div className="hero-inner">
          <div className="hero-logo" aria-hidden="true" />
          <h1 className="wordmark">Sch&#275;ma</h1>
          <p className="hero-sub" data-testid="home-hero-sub">
            {c("home.hero.sub", "A community of Christian artists")}
          </p>
          <p className="hero-line" data-testid="home-hero-line">
            {c(
              "home.hero.line",
              "A context for Christian art and home for Christian artists. Together we are building a path forward for human creativity.",
            )}
          </p>
        </div>
        <a className="scroll-cue" href="#what">
          <span>{c("home.hero.cue", "Begin")}</span>
        </a>
      </header>

      <NextStrip />

      <section id="what" className="long-read" data-testid="home-what">
        <div className="prose">
          <p className="eyebrow sc">
            <span className="num">&middot;</span>
            <span>{c("home.what.eyebrow", "What this is")}</span>
          </p>
          <h2 data-testid="home-what-heading">
            {c("home.what.heading", "We\u2019d like to be the room.")}
          </h2>
          <p className="lede">
            {c(
              "home.what.lede",
              "Most artists we know are working alone. Not by preference \u2014 by default. The people who would once have received a piece of work, sat with it, argued about it, and told you the truth about it have largely been replaced by an audience that scrolls.",
            )}
          </p>
          <p>
            {c(
              "home.what.body",
              "Sch\u0113ma is an answer to the isolation \u2014 and to the cultural haze pushing human creativity down inhuman paths. Art is best made in community, and for community. We believe this is all the more true for those of us who want to make and think about art while inhabiting the Christian story. Put it together and you get Sch\u0113ma: a community for Christian artists, a path to develop as a maker, and a context for reception \u2014 not consumption. You don\u2019t have to be a seasoned creator to join \u2014 people of all skills and disciplines are welcome. We hold large gatherings to show and receive work, circles where you can develop and learn, and special events. Come!",
            )}
          </p>
        </div>
      </section>

      <ScatterBand />

      <section data-testid="home-doors">
        <div className="wrap">
          <p className="eyebrow sc">
            <span className="num">&middot;</span>
            <span>{c("home.ways.eyebrow", "Ways in")}</span>
          </p>
          <div className="doors">
            {doors.map((d) => (
              <Link
                key={d.to}
                to={d.to}
                className="door"
                data-testid={d.testid}
              >
                <div className={`door-icon ${d.icon}`} aria-hidden="true">
                  <i />
                </div>
                <h3>{d.title}</h3>
                <p>{d.body}</p>
                <span className="go">{d.go} &rarr;</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Rule />

      <section data-testid="home-open-door">
        <div className="wrap">
          <div className="sending">
            <div className="art" aria-hidden="true" />
            <div>
              <p className="eyebrow sc">
                <span className="num">&middot;</span>
                <span>{c("home.entry.eyebrow", "The entry process")}</span>
              </p>
              <h2>{c("home.entry.heading", "Come to something.")}</h2>
              <p>
                {c(
                  "home.entry.body1",
                  "That\u2019s it. That\u2019s the whole process. There\u2019s nothing to sign, no application, and no portfolio. Come once, see whether the room is any good, come again.",
                )}
              </p>
              <p>
                {c(
                  "home.entry.body2",
                  "And we\u2019re not recruiting you away from anywhere. Whatever congregation you belong to, keep belonging to it \u2014 this is the workshop, not the sanctuary.",
                )}
              </p>
              <div className="actions">
                <Link to="/join" className="act" data-testid="home-join-cta">
                  {c("home.entry.cta", "Get in touch")} &rarr;
                </Link>
                <Link
                  to="/circles"
                  className="linkish"
                  data-testid="home-circles-link"
                >
                  {c("home.entry.secondary", "Or find your circle")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Rule />

      <section data-testid="home-newsletter">
        <div className="prose">
          <Newsletter source="home" testidPrefix="home-newsletter" />
        </div>
      </section>

      <Verse />
      <Footer />
    </>
  );
}
